import React, { useEffect, useState } from "react";
import {
  Description,
  FieldGroup,
  Fieldset,
  Label,
} from "../../../../design/fieldset";
import { Heading, Subheading } from "../../../../design/heading";
import { Switch, SwitchField } from "../../../../design/switch";
import { Input } from "../../../../design/input";
import { Textarea } from "../../../../design/textarea";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import { Divider } from "../../../../design/divider";
import { useAccount } from "../../../../contexts/AccountContext";
import { useBusiness } from "../../../../contexts/BusinessContext";
import { AccountService } from "../../../../services/AccountService";
import SettingsHeader from "../SettingsHeader";
import LogoUpload from "../../../../components/LogoUpload";
import { CheckInBranding } from "@visitproof/types";
import {
  DeviceTabletIcon,
  PaintBrushIcon,
  CameraIcon,
  DocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../../firebase";

const DEFAULT_BRANDING: CheckInBranding = {
  welcomeMessage: "",
  primaryColor: "",
  kioskLogoUrl: "",
};

const CheckInSettings: React.FC = () => {
  const { account } = useAccount();
  const { business } = useBusiness();
  const [branding, setBranding] = useState<CheckInBranding>(DEFAULT_BRANDING);
  const [requirePhoto, setRequirePhoto] = useState(false);
  const [kioskLogo, setKioskLogo] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Initialize from account data
  useEffect(() => {
    if (account?.checkInBranding) {
      setBranding({
        ...DEFAULT_BRANDING,
        ...account.checkInBranding,
      });
    }
    const features = account?.settings?.features;
    setRequirePhoto(features?.requirePhotoOnCheckIn ?? false);
  }, [account]);

  const handlePhotoToggle = async (checked: boolean) => {
    if (!account?.id) return;
    setRequirePhoto(checked);
    try {
      await AccountService.updateAccount(account.id, {
        settings: {
          ...account.settings,
          features: {
            ...account.settings?.features,
            requirePhotoOnCheckIn: checked,
          },
        },
      } as any);
    } catch (e) {
      console.error("Error updating photo setting:", e);
      setRequirePhoto(!checked);
    }
  };

  const handleTextChange = (field: keyof CheckInBranding, value: string) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!account?.id) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      let updatedBranding = { ...branding };

      // Upload kiosk logo if a new one was selected
      if (kioskLogo) {
        const fileRef = ref(
          storage,
          `accounts/${account.id}/kiosk-logo/${kioskLogo.name}`
        );
        await uploadBytes(fileRef, kioskLogo);
        const url = await getDownloadURL(fileRef);
        updatedBranding.kioskLogoUrl = url;
      }

      await AccountService.updateAccount(account.id, {
        checkInBranding: updatedBranding,
      });

      setBranding(updatedBranding);
      setKioskLogo(null);
      setSaveMessage({ type: "success", text: "Check-in settings saved" });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error("Error saving check-in settings:", error);
      setSaveMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setIsSaving(false);
    }
  };

  const currentLogoUrl =
    branding.kioskLogoUrl || business?.logoUrl || null;

  return (
    <>
      <SettingsHeader />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <Heading>Check-In Kiosk</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Configure the check-in kiosk app that runs on a tablet at your
            facility. Parents sign in when they arrive for visits.
          </Text>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`p-3 rounded-lg text-sm ${
              saveMessage.type === "success"
                ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        {/* Branding */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
              <PaintBrushIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <Subheading>Branding</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            Customize the look of the check-in kiosk with your organization's
            branding. Parents will see this when they arrive.
          </Text>

          <FieldGroup>
            <div>
              <Label>Kiosk Logo</Label>
              <Text className="mb-3 text-sm text-zinc-600 dark:text-zinc-400">
                Upload a logo for the check-in screen. If not set, your business
                logo will be used.
              </Text>
              <LogoUpload
                currentLogoUrl={currentLogoUrl}
                onFileSelected={setKioskLogo}
              />
            </div>

            <div>
              <Label>Welcome Message</Label>
              <Text className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">
                Displayed on the check-in screen when parents arrive.
              </Text>
              <Textarea
                name="welcomeMessage"
                placeholder="e.g., Welcome to The BLU House! Please sign in below."
                rows={2}
                value={branding.welcomeMessage || ""}
                onChange={(e) =>
                  handleTextChange("welcomeMessage", e.target.value)
                }
              />
            </div>

            <div className="max-w-xs">
              <Label>Accent Color</Label>
              <Text className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">
                Primary color for buttons and highlights on the kiosk.
              </Text>
              <div className="flex items-center gap-3">
                <Input
                  name="primaryColor"
                  type="text"
                  placeholder="#1E3C5A"
                  value={branding.primaryColor || ""}
                  onChange={(e) =>
                    handleTextChange("primaryColor", e.target.value)
                  }
                  className="flex-1"
                />
                {branding.primaryColor && (
                  <div
                    className="w-10 h-10 rounded-lg border border-zinc-300 dark:border-zinc-600 flex-shrink-0"
                    style={{ backgroundColor: branding.primaryColor }}
                  />
                )}
              </div>
            </div>
          </FieldGroup>
        </Fieldset>

        <Divider />

        {/* Check-In Features */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <DocumentCheckIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <Subheading>Check-In Features</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            Configure what happens when a parent checks in at the kiosk.
          </Text>

          <FieldGroup>
            <SwitchField>
              <Label>Signature Required</Label>
              <Description>
                Parents must sign on the tablet screen when checking in. The
                signature is timestamped and stored with the visit record.
              </Description>
              <Switch
                name="signatureRequired"
                checked={true}
                onChange={() => {}}
                disabled
              />
            </SwitchField>

            <SwitchField>
              <Label>Photo Capture</Label>
              <Description>
                Take a photo of each person when they check in. Photos are
                stored securely and linked to the visit sign-in sheet.
              </Description>
              <Switch
                name="requirePhotoOnCheckIn"
                checked={requirePhoto}
                onChange={handlePhotoToggle}
              />
            </SwitchField>
          </FieldGroup>
        </Fieldset>

        <Divider />

        {/* Preview */}
        <Fieldset>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-sky-50 dark:bg-sky-900/20">
              <DeviceTabletIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
            </div>
            <Subheading>Preview</Subheading>
          </div>
          <Text className="mb-6 text-zinc-600 dark:text-zinc-400">
            A preview of how the check-in kiosk will look to visitors.
          </Text>

          <div
            className="max-w-sm mx-auto border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-hidden"
            style={{
              backgroundColor: "#ffffff",
            }}
          >
            <div
              className="p-6 text-center"
              style={{
                backgroundColor: branding.primaryColor || "#1E3C5A",
              }}
            >
              {currentLogoUrl ? (
                <img
                  src={currentLogoUrl}
                  alt="Kiosk logo"
                  className="h-12 mx-auto mb-3 object-contain"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              ) : (
                <div className="text-xl font-bold text-white mb-1">
                  {business?.name || "Your Organization"}
                </div>
              )}
              <div className="text-sm text-white/80">Check-In</div>
            </div>
            <div className="p-6">
              <div className="text-sm text-zinc-600 text-center mb-4">
                {branding.welcomeMessage ||
                  "Welcome! Please sign in below."}
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                  <div className="w-5 h-5 rounded border-2 border-zinc-300" />
                  <span className="text-sm text-zinc-700">Jane Smith</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-5 h-5 rounded bg-green-500 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm text-green-700 font-medium">
                      John Doe
                    </span>
                    <span className="text-xs text-green-600 block">
                      Signed in at 2:30 PM
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Fieldset>

        {/* Save Button */}
        <div className="flex items-center gap-4">
          <Button color="blue" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            Photo capture toggle saves automatically. Click "Save Changes" to
            save branding updates.
          </Text>
        </div>

        <Divider />

        {/* Setup Instructions */}
        <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
          <Subheading className="mb-2">Setting Up the Kiosk</Subheading>
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            Install the "VisitProof Check-In" app on a tablet at your facility.
            Sign in with a staff account, and the tablet will display today's
            visits. When parents arrive, they tap their name, sign on the screen,
            and the check-in is recorded with a timestamp.
          </Text>
          <Text className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            Supervisors receive a notification when all parents for a visit have
            signed in.
          </Text>
        </div>
      </div>
    </>
  );
};

export default CheckInSettings;
